import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { brandColors } from '@/theme/brandColors';
import { LEGAL_VERSIONS } from '@/legal/versions';

type Props = {
  open: boolean;
  onAccept: () => Promise<void>;
};

/**
 * Blocking modal shown when the user hasn't accepted the current version of
 * the Terms of Service and/or Privacy Policy. The user must check both
 * checkboxes before the "Zustimmen" button becomes active.
 */
export function LegalConsentModal({ open, onAccept }: Props) {
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSubmit = termsChecked && privacyChecked;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setLoading(true);
    await onAccept();
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      aria-labelledby="legal-consent-title"
    >
      <DialogTitle id="legal-consent-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: `rgba(${brandColors.primaryRgb}, 0.1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <GavelIcon sx={{ fontSize: 20, color: brandColors.primary }} />
          </Box>
          <Typography variant="h6" component="span" fontWeight={700} fontSize="1.05rem">
            Aktualisierte Nutzungsbedingungen
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Wir haben unsere Nutzungsbedingungen und Datenschutzerklärung aktualisiert.
          Bitte lies die Dokumente und stimme ihnen zu, um ImmoKompass weiterhin nutzen zu können.
        </Typography>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {/* Terms row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <GavelIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                Allgemeine Geschäftsbedingungen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Version {LEGAL_VERSIONS.terms} ·{' '}
                <Link component={RouterLink} to="/agb" target="_blank" rel="noopener">
                  Lesen
                </Link>
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                  size="small"
                  sx={{ color: brandColors.primary, '&.Mui-checked': { color: brandColors.primary } }}
                />
              }
              label=""
              sx={{ m: 0, mr: -0.5 }}
            />
          </Box>

          {/* Privacy row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <PrivacyTipOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                Datenschutzerklärung
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Version {LEGAL_VERSIONS.privacy} ·{' '}
                <Link component={RouterLink} to="/datenschutz" target="_blank" rel="noopener">
                  Lesen
                </Link>
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyChecked}
                  onChange={(e) => setPrivacyChecked(e.target.checked)}
                  size="small"
                  sx={{ color: brandColors.primary, '&.Mui-checked': { color: brandColors.primary } }}
                />
              }
              label=""
              sx={{ m: 0, mr: -0.5 }}
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Durch Klicken auf „Zustimmen" bestätigst du, dass du beide Dokumente gelesen hast
          und ihnen zustimmst.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          variant="contained"
          disabled={!canSubmit || loading}
          onClick={() => void handleAccept()}
          sx={{ minWidth: 120, textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Wird gespeichert…' : 'Zustimmen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
